import React, {useMemo, useState} from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import FlexBox from "view/components/Layout/FlexBox";
import {StandsWithCompanies} from "ts/api/chat";
import {useCurrentShowId, useDebouncedValue} from "hooks";
import useInfiniteExhibitorPairs from "hooks/exhibitorPair/useInfiniteExhibitorPairs";
import useInfiniteListbox from "hooks/ui/useInfiniteListbox";
import {fetchExhibitorPairsByStandIds} from "hooks/exhibitorPair/exhibitorPairQueries";
import groupPairsByStand from "tools/chat/groupPairsByStand";

type SearchProps = {
  createConversation: (data: StandsWithCompanies) => void;
  text?: string;
};

const keepAll = (options: StandsWithCompanies[]) => options;

const Search = ({text, createConversation}: SearchProps) => {
  const showId = useCurrentShowId();
  const [search, setSearch] = useDebouncedValue("", 300);
  const [isResolving, setIsResolving] = useState(false);

  const {
    pairs,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteExhibitorPairs({showId, search});

  const options = useMemo(() => groupPairsByStand(pairs), [pairs]);

  const {listboxProps, reset: resetListbox} = useInfiniteListbox({
    items: options,
    hasMore: hasNextPage,
    loading: isFetching,
    onLoadMore: fetchNextPage,
  });

  const onOptionSelect = async (option: StandsWithCompanies) => {
    setIsResolving(true);
    try {
      const standPairs = await fetchExhibitorPairsByStandIds({
        showId,
        legacyStandIds: [option.stand.standId],
      });
      const [stand] = groupPairsByStand(standPairs);
      createConversation(stand ?? option);
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <FlexBox
      alignItems="center"
      width="100%"
      justifyContent="space-between"
      borderBottom="1px solid rgba(0, 0, 0, 0.15)"
      padding="24px"
    >
      <Box fontSize={18}>New Message:</Box>

      <Autocomplete
        options={options}
        filterOptions={keepAll}
        loading={isFetching || isResolving}
        ListboxProps={listboxProps}
        getOptionLabel={(option) => {
          if (option.stand.suffixName) {
            return `(${option.stand.name} (${option.stand.suffixName})) ${option.allCompaniesNames}`;
          }

          return `(${option.stand.name}) ${option.allCompaniesNames}`;
        }}
        style={{width: "80%", height: "48px"}}
        onInputChange={(_, newSearchValue, reason) => {
          if (reason === "reset") return;
          resetListbox();
          setSearch(newSearchValue);
        }}
        onClose={resetListbox}
        onChange={(_, stand) => {
          if (stand) {
            onOptionSelect(stand);
          }
        }}
        renderInput={(params) => (
          <TextField
            style={{height: "48px"}}
            {...params}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isFetching || isResolving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FlexBox>
  );
};

export default Search;
